import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, NavBar } from "antd-mobile";
import { useLocation, useNavigate } from "react-router-dom";
import { getBottomNavBarContainerStyle, getNavBarContainerStyle } from "../../../../utils/deviceUtils";
import AddressCoordinateFeedbackMapView from "../../address-coordinate-feedback/components/AddressCoordinateFeedbackMapView";
import { popBack } from "../../../../utils/appRuntime";
import { t } from "../../../../i18n";
import { useNativeLangSync } from "../../../../utils/langUtils";

const DEFAULT_MAPBOX_API_KEY = "pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ";
const DEFAULT_LAT = 33.707788;
const DEFAULT_LNG = -84.410601;
const MAP_PICKER_RESULT_KEY = "delivery_route_endpoints_map_picker_result";

function parseCoordinateParam(value: string | null, type: "lat" | "lng"): number | null {
    if (value == null || value.trim() === "") {
        return null;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return null;
    }

    if (type === "lat" && (parsed < -90 || parsed > 90)) {
        return null;
    }

    if (type === "lng" && (parsed < -180 || parsed > 180)) {
        return null;
    }

    return parsed;
}

type EndpointFields = {
    country: string;
    state: string;
    city: string;
    district: string;
    postalCode: string;
    detailAddress: string;
};

type MapboxFeature = {
    id?: string;
    text?: string;
    place_name?: string;
    context?: Array<{ id?: string; text?: string; short_code?: string }>;
};

const EMPTY_ENDPOINT_FIELDS: EndpointFields = {
    country: "",
    state: "",
    city: "",
    district: "",
    postalCode: "",
    detailAddress: "",
};

async function requestMapboxFeature(url: string): Promise<MapboxFeature | undefined> {
    const response = await fetch(url);
    const data = await response.json();
    const features = data?.features as MapboxFeature[] | undefined;
    if (!features?.length) {
        return undefined;
    }
    return features[0];
}

async function reverseGeocodeFeature(lat: number, lng: number): Promise<MapboxFeature | undefined> {
    const addressOnlyUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${DEFAULT_MAPBOX_API_KEY}&types=address&limit=1`;
    const fallbackUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${DEFAULT_MAPBOX_API_KEY}&limit=1`;

    const addressFeature = await requestMapboxFeature(addressOnlyUrl);
    if (addressFeature) {
        return addressFeature;
    }

    return requestMapboxFeature(fallbackUrl);
}

function toSpaceSeparatedAddress(value: string): string {
    return value
        .replace(/,/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function mergeDetailAddress(centerLabel: string, detailAddress: string): string {
    const normalizedLabel = toSpaceSeparatedAddress(centerLabel);
    const normalizedDetail = toSpaceSeparatedAddress(detailAddress);

    if (!normalizedLabel) {
        return normalizedDetail;
    }
    if (!normalizedDetail) {
        return normalizedLabel;
    }

    const lowerLabel = normalizedLabel.toLowerCase();
    const lowerDetail = normalizedDetail.toLowerCase();
    if (lowerDetail.includes(lowerLabel)) {
        return normalizedDetail;
    }
    if (lowerLabel.includes(lowerDetail)) {
        return normalizedLabel;
    }

    // 希望 detailAddress 更像 “街道 + 建筑名”
    return toSpaceSeparatedAddress(`${normalizedDetail} ${normalizedLabel}`);
}

function removePostalCodeFromDetailAddress(detailAddress: string, postalCode?: string): string {
    let cleaned = toSpaceSeparatedAddress(detailAddress);
    const normalizedPostal = toSpaceSeparatedAddress(postalCode ?? "");

    if (normalizedPostal) {
        cleaned = toSpaceSeparatedAddress(
            cleaned.replace(new RegExp(`\\b${normalizedPostal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g"), " ")
        );
    }

    // 兜底：去掉开头的纯数字邮编（4-10位），避免未识别 postalCode 时仍残留。
    cleaned = toSpaceSeparatedAddress(cleaned.replace(/^\d{4,10}\s+/g, ""));
    return cleaned;
}

function composeDisplayAddress(fields: EndpointFields, centerLabel: string, fallback: string): string {
    const fallbackParts = fallback
        .split(",")
        .map((item) => toSpaceSeparatedAddress(item))
        .filter(Boolean);

    const fallbackPostal =
        fallbackParts.length > 0 && /^\d{4,10}$/.test(fallbackParts[0].replace(/\s+/g, ""))
            ? fallbackParts[0].replace(/\s+/g, "")
            : "";
    const fallbackCountry = fallbackParts.length > 0 ? fallbackParts[fallbackParts.length - 1] : "";
    const fallbackCity = fallbackParts.length >= 3 ? fallbackParts[fallbackParts.length - 2] : "";
    const fallbackDistrict = fallbackParts.length >= 4 ? fallbackParts[fallbackParts.length - 3] : "";

    const postalCode = toSpaceSeparatedAddress(fields.postalCode || fallbackPostal);
    const country = toSpaceSeparatedAddress(fields.country || fallbackCountry);
    const city = toSpaceSeparatedAddress(fields.city || fallbackCity);
    const district = toSpaceSeparatedAddress(fields.district || fallbackDistrict);
    const state = toSpaceSeparatedAddress(fields.state);

    const primaryRaw = mergeDetailAddress(centerLabel, fields.detailAddress);
    // 显示时将邮编统一放到最后，避免出现在前缀位置。
    const primary = postalCode
        ? toSpaceSeparatedAddress(
              primaryRaw.replace(new RegExp(`\\b${postalCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g"), " ")
          )
        : primaryRaw;

    let primaryBlock = primary;
    if (!primaryBlock) {
        primaryBlock = district;
    } else if (district && !primaryBlock.toLowerCase().includes(district.toLowerCase())) {
        primaryBlock = toSpaceSeparatedAddress(`${primaryBlock} ${district}`);
    }

    const zipCountry = toSpaceSeparatedAddress(
        postalCode && country
            ? `${postalCode} ${country}`
            : postalCode || country || ""
    );

    // 固定顺序：详细地址/区 -> 城市 -> 州 -> 邮编-国家
    const orderedParts = [primaryBlock, city, state, zipCountry]
        .map((item) => toSpaceSeparatedAddress(item ?? ""))
        .filter(Boolean);

    if (!orderedParts.length) {
        return toSpaceSeparatedAddress(fallback);
    }

    const deduped: string[] = [];
    const seen = new Set<string>();
    for (const part of orderedParts) {
        const key = part.toLowerCase();
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        deduped.push(part);
    }
    return deduped.join(" ");
}

function parseMapboxFeatureToEndpoint(feature: MapboxFeature): EndpointFields {
    const context = (feature?.context ?? []) as Array<{ id?: string; text?: string; short_code?: string }>;

    const findContextText = (prefixes: string[]): string => {
        const item = context.find((c) => {
            const id = c?.id;
            return id && prefixes.some((p) => id.startsWith(`${p}.`));
        });
        return (item?.text ?? "") as string;
    };

    const findContextShortCode = (prefixes: string[]): string => {
        const item = context.find((c) => {
            const id = c?.id;
            return id && prefixes.some((p) => id.startsWith(`${p}.`));
        });
        return (item?.short_code ?? item?.text ?? "") as string;
    };

    const placeText = (feature?.text ?? "") as string;
    const placeName = (feature?.place_name ?? "") as string;
    const placeNameParts = placeName
        ? placeName
            .split(",")
            .map((s: string) => s.trim())
            .filter(Boolean)
        : [];

    const postcode = findContextText(["postcode"]);
    // detailAddress 保持“街道/建筑主体”，不要把 city/state/postcode/country 全塞进来。
    const detailAddressCandidate = placeText || placeName.split(",")[0]?.trim() || "";
    const detailAddress = removePostalCodeFromDetailAddress(detailAddressCandidate, postcode);

    // state 在 context 里通常有 region.short_code（如 GA），也可能只有 region.text（如 Georgia）
    const regionShort = findContextShortCode(["region"]);
    const regionText = findContextText(["region"]);
    let state = regionShort.length === 2 ? regionShort : regionText;

    // context 不全时的兜底：从 place_name 拆分
    if (!state && placeNameParts.length >= 2) {
        const stateZipPart = placeNameParts[placeNameParts.length - 2];
        const m = /\b([A-Z]{2})\b/.exec(stateZipPart);
        if (m?.[1]) state = m[1];
    }

    let country = findContextText(["country"]);
    if (!country && placeNameParts.length >= 1) {
        country = placeNameParts[placeNameParts.length - 1];
    }

    let city = findContextText(["place"]) || findContextText(["locality"]);

    let district =
        findContextText(["localadmin"]) ||
        findContextText(["neighborhood"]) ||
        findContextText(["district"]);

    // 非英文地址场景兜底：很多返回是 "postcode, district, city, country"
    if (!district && placeNameParts.length >= 2) {
        district = placeNameParts[1];
    }

    if (!city) {
        if (placeNameParts.length >= 3) {
            city = placeNameParts[2];
        } else if (placeNameParts.length >= 2) {
            city = placeNameParts[1];
        }
    }

    return {
        country,
        state: state || city,
        city,
        district,
        postalCode: postcode,
        detailAddress,
    };
}

const RouteEndpointsMapPickerNativePage: React.FC = () => {
    useNativeLangSync();
    const navigate = useNavigate();
    const location = useLocation();
    const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const section = (search.get("section") === "end" ? "end" : "start") as "start" | "end";
    const pageTitle = search.get("title") || t("pages.delivery.routeEndpoints.mapPicker.title");
    const initialAddress = search.get("address") || "";
    const initialLat = useMemo(() => parseCoordinateParam(search.get("lat"), "lat"), [search]);
    const initialLng = useMemo(() => parseCoordinateParam(search.get("lng"), "lng"), [search]);
    const hasInitialCoordinates = initialLat != null && initialLng != null;

    const [selectedLat, setSelectedLat] = useState<number | null>(hasInitialCoordinates ? initialLat : null);
    const [selectedLng, setSelectedLng] = useState<number | null>(hasInitialCoordinates ? initialLng : null);
    const [centerLabel, setCenterLabel] = useState<string>("");
    const [resolvedAddress, setResolvedAddress] = useState<string>(initialAddress);
    const [endpointFields, setEndpointFields] = useState<EndpointFields>(EMPTY_ENDPOINT_FIELDS);
    const debounceTimerRef = useRef<number | null>(null);
    const reverseGeocodeRequestIdRef = useRef(0);
    const [isLocating, setIsLocating] = useState(!hasInitialCoordinates);
    const [isResolvingAddress, setIsResolvingAddress] = useState(false);

    useEffect(() => {
        if (hasInitialCoordinates) {
            setIsLocating(false);
            return;
        }

        let cancelled = false;

        const fallbackToDefault = () => {
            if (cancelled) {
                return;
            }

            setSelectedLat((prev) => prev ?? DEFAULT_LAT);
            setSelectedLng((prev) => prev ?? DEFAULT_LNG);
            setIsLocating(false);
        };

        if (!navigator.geolocation) {
            fallbackToDefault();
            return () => {
                cancelled = true;
            };
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (cancelled) {
                    return;
                }

                setSelectedLat(position.coords.latitude);
                setSelectedLng(position.coords.longitude);
                setIsLocating(false);
            },
            (error) => {
                console.warn("get current position failed", error);
                fallbackToDefault();
            },
            {
                enableHighAccuracy: true,
                maximumAge: 30000,
                timeout: 8000,
            }
        );

        return () => {
            cancelled = true;
        };
    }, [hasInitialCoordinates]);

    useEffect(() => {
        if (selectedLat == null || selectedLng == null) {
            return;
        }

        reverseGeocodeRequestIdRef.current += 1;
        const requestId = reverseGeocodeRequestIdRef.current;
        setIsResolvingAddress(true);

        if (debounceTimerRef.current) {
            window.clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = window.setTimeout(async () => {
            try {
                const feature = await reverseGeocodeFeature(selectedLat, selectedLng);

                if (requestId !== reverseGeocodeRequestIdRef.current) {
                    return;
                }

                const placeName = feature?.place_name as string | undefined;
                if (placeName) {
                    setResolvedAddress(placeName);
                } else {
                    setResolvedAddress("");
                }
                if (feature) {
                    setEndpointFields(parseMapboxFeatureToEndpoint(feature));
                } else {
                    setEndpointFields(EMPTY_ENDPOINT_FIELDS);
                }
            } catch (error) {
                if (requestId !== reverseGeocodeRequestIdRef.current) {
                    return;
                }
                // keep current address on lookup failure
                console.warn("reverse geocode failed", error);
            } finally {
                if (requestId === reverseGeocodeRequestIdRef.current) {
                    setIsResolvingAddress(false);
                }
            }
        }, 150);
        return () => {
            if (debounceTimerRef.current) {
                window.clearTimeout(debounceTimerRef.current);
            }
        };
    }, [selectedLat, selectedLng]);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        popBack();
    };

    const handleCancel = () => handleBack();

    const handleConfirm = () => {
        if (selectedLat == null || selectedLng == null) {
            return;
        }

        const preferredDetailAddress = removePostalCodeFromDetailAddress(
            mergeDetailAddress(centerLabel, endpointFields.detailAddress || resolvedAddress || ""),
            endpointFields.postalCode
        );

        sessionStorage.setItem(
            MAP_PICKER_RESULT_KEY,
            JSON.stringify({
                section,
                confirmedAt: Date.now(),
                latitude: selectedLat,
                longitude: selectedLng,
                ...endpointFields,
                detailAddress: preferredDetailAddress,
            })
        );
        handleBack();
    };

    const displayAddress = composeDisplayAddress(endpointFields, centerLabel, resolvedAddress);

    if (isLocating || selectedLat == null || selectedLng == null) {
        return (
            <div className="flex flex-col h-screen bg-white">
                <div {...getNavBarContainerStyle()}>
                    <NavBar
                        onBack={handleBack}
                        style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            background: '#fff',
                        }}
                    >
                        {pageTitle}
                    </NavBar>
                </div>
                <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">
                    {t("map.loading")}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-white">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={handleBack}
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: '#fff',
                    }}
                >
                    {pageTitle}
                </NavBar>
            </div>

            <div className="flex-1 min-h-0">
                <AddressCoordinateFeedbackMapView
                    latitude={selectedLat}
                    longitude={selectedLng}
                    onCoordinateChange={(nextLat, nextLng) => {
                        setSelectedLat(nextLat);
                        setSelectedLng(nextLng);
                    }}
                    onCenterLabelChange={(label) => {
                        setCenterLabel(label);
                    }}
                    showCenterIndicator
                    enableDragToSelect
                    zoom={15}
                    stopNumber={null}
                />
            </div>

            <Card className="flex-0 m-2 rounded-lg">
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                        <p className="text-sm font-medium text-gray-600">{t("pages.delivery.routeEndpoints.mapPicker.currentCoordinate")}</p>
                        <div className="w-full bg-gray-100 rounded-md px-3 py-2 text-base text-gray-700 break-words">
                            {`${selectedLat.toFixed(7)}, ${selectedLng.toFixed(7)}`}
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <p className="text-sm font-medium text-gray-600">{t("pages.delivery.routeEndpoints.mapPicker.currentAddress")}</p>
                        <div className="w-full bg-gray-100 rounded-md px-3 py-2 text-base text-gray-700 break-words">
                            {displayAddress || (isResolvingAddress ? t("map.loading") : "-")}
                        </div>
                    </div>
                </div>
            </Card>

            <div {...getBottomNavBarContainerStyle("flex-0 flex px-4 py-2 mt-auto gap-4")}>
                <Button
                    color="default"
                    onClick={handleCancel}
                    block
                    style={{
                        "--background-color": "gray",
                        "--text-color": "white",
                    }}
                >
                    {t("pages.delivery.routeEndpoints.mapPicker.cancel")}
                </Button>
                <Button color="primary" onClick={handleConfirm} block>
                    {t("pages.delivery.routeEndpoints.mapPicker.confirm")}
                </Button>
            </div>
        </div>
    );
};

export default RouteEndpointsMapPickerNativePage;
